import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAbrirNovo } from '../hooks/useAbrirNovo';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuX, LuChevronLeft, LuChevronRight, LuPencil, LuEye, LuTrash2, LuUser,
} from 'react-icons/lu';
import { apiFetch } from '../services/api';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import {
    ListRow, PerPage, RowAction, RowActions, RowConfirm, ListCard, ListCards, CardConfirm,
} from '../components/ListKit';
import Dialog from '../components/Dialog';
import Select from '../components/Select';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
// Global disfarçado — ver o comentário equivalente em ContactsPage.tsx.
import './TeamsPage.css';
import EquipeService, { type Equipe } from '../services/EquipeService';

const apiUrl = import.meta.env.VITE_API_URL;
const POR_PAGINA = [25, 50, 100];

type Modo = 'ver' | 'editar';
interface Pessoa { id: number; nome: string }

interface Rascunho { id: number | null; nome: string; liderId: number }
const VAZIO: Rascunho = { id: null, nome: '', liderId: 0 };

const iniciais = (nome: string) => nome.trim().split(/\s+/).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

const TeamsPage = () => {
    const celular = useCelular();
    const [equipes, setEquipes] = useState<Equipe[]>([]);
    const [pessoas, setPessoas] = useState<Pessoa[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    /* Recorte de trabalho: abrir uma equipe, voltar e reencontrar a lista. */
    const [recorte, gravarRecorte] = useRecorte('eq:recorte', {
        busca: '', pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, pagina, porPagina });
    }, [busca, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<Equipe | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [rascunho, setRascunho] = useState<Rascunho>(VAZIO);
    const [salvando, setSalvando] = useState(false);
    const [erros, setErros] = useState<Record<string, string>>({});
    const [mexendo, setMexendo] = useState(false);
    const [seletor, setSeletor] = useState(false);
    const refSeletor = useRef<HTMLDivElement | null>(null);

    /* A lixeira da linha pergunta NA linha; o rodapé do painel abre o
       `<Dialog>`, porque ali não existe linha para pintar. */
    const [aConfirmar, setAConfirmar] = useState<number | null>(null);
    const [confirmarNoPainel, setConfirmarNoPainel] = useState(false);

    /* ─── Dados ──────────────────────────────────────────────── */

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const [eq, ru] = await Promise.all([
                EquipeService.buscarTodas(),
                apiFetch(`${apiUrl}/api/Usuario/BuscarTodos`),
            ]);
            setEquipes(eq);
            const du = await ru.json();
            if (du.sucesso) {
                setPessoas((du.dados || []).map((u: any) => ({ id: u.userId, nome: u.userName })));
            }
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível carregar as equipes.'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, porPagina]);

    useEffect(() => {
        if (!seletor) return;
        const fora = (e: MouseEvent) => {
            if (refSeletor.current && !refSeletor.current.contains(e.target as Node)) setSeletor(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [seletor]);

    const nomeDe = useCallback((id: number) => pessoas.find(p => p.id === id)?.nome, [pessoas]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return t ? equipes.filter(e => e.nome.toLowerCase().includes(t)) : equipes;
    }, [equipes, busca]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    /* ─── Painel ─────────────────────────────────────────────── */

    const abrirVer = (e: Equipe) => { setAberto(e); setModo('ver'); setErros({}); setSeletor(false); };
    const abrirEdicao = (e: Equipe) => {
        setAberto(e);
        setRascunho({ id: e.id, nome: e.nome, liderId: e.liderId });
        setModo('editar'); setErros({});
    };
    /* A paleta do Ctrl+K pediu "Nova equipe": abre o formulário direto. */
    useAbrirNovo(() => abrirNova());

    const abrirNova = () => {
        setAberto({ id: 0, nome: '', liderId: 0, liderNome: '', membroIds: [], membros: [] });
        setRascunho(VAZIO); setModo('editar'); setErros({});
    };
    const fechar = () => { setAberto(null); setErros({}); setSeletor(false); };

    const ehNova = modo === 'editar' && rascunho.id === null;

    const salvar = async () => {
        const e: Record<string, string> = {};
        if (!rascunho.nome.trim()) e.nome = 'Informe o nome da equipe.';
        if (!rascunho.liderId) e.liderId = 'Escolha o líder.';
        setErros(e);
        if (Object.keys(e).length) return;

        setSalvando(true);
        try {
            // O líder entra como membro: liderId apontando para quem não é
            // membro é um estado que a API aceita e ninguém enxerga depois.
            const membros = aberto?.membroIds ?? [];
            const membroIds = membros.includes(rascunho.liderId) ? membros : [...membros, rascunho.liderId];
            const dto = { nome: rascunho.nome.trim(), liderId: rascunho.liderId, membroIds };

            if (rascunho.id) await EquipeService.atualizar(rascunho.id, dto);
            else await EquipeService.criar(dto);

            aviso.ok(rascunho.id ? 'Equipe atualizada.' : 'Equipe criada.');
            await carregar();
            fechar();
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível salvar a equipe.'));
        } finally {
            setSalvando(false);
        }
    };

    /* `alvo` opcional porque a exclusão tem duas portas: a lixeira da linha
       (que manda a equipe) e o rodapé do painel (que usa o que está em edição). */
    const excluir = async (alvo?: Equipe) => {
        const id = alvo?.id ?? rascunho.id;
        if (!id) return;
        setSalvando(true);
        try {
            await EquipeService.excluir(id);
            aviso.ok('Equipe excluída.');
            await carregar();
            fechar();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível excluir a equipe.'));
        } finally {
            setSalvando(false);
            setAConfirmar(null);
            setConfirmarNoPainel(false);
        }
    };

    /* ─── Membros — o mesmo vínculo da tela de Usuários, pelo outro lado.
       Relê antes de salvar porque o DTO manda o registro inteiro. */

    const mexerNosMembros = async (mudar: (ids: number[]) => number[], msg: string) => {
        const id = aberto?.id;
        if (!id) return;
        setMexendo(true);
        try {
            const atuais = await EquipeService.buscarTodas();
            const alvo = atuais.find(e => e.id === id);
            if (!alvo) {
                aviso.atencao('Essa equipe não existe mais.');
                setEquipes(atuais); fechar();
                return;
            }
            await EquipeService.atualizar(alvo.id, {
                nome: alvo.nome, liderId: alvo.liderId, membroIds: mudar(alvo.membroIds ?? []),
            });
            aviso.ok(msg);
            const novas = await EquipeService.buscarTodas();
            setEquipes(novas);
            setAberto(novas.find(e => e.id === id) ?? null);
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível atualizar a equipe.'));
        } finally {
            setMexendo(false);
            setSeletor(false);
        }
    };

    const membrosDaAberta: Pessoa[] = useMemo(() => {
        if (!aberto) return [];
        return (aberto.membroIds ?? []).map(id => ({ id, nome: nomeDe(id) ?? `Usuário #${id}` }));
    }, [aberto, nomeDe]);

    const disponiveis = useMemo(
        () => pessoas.filter(p => !(aberto?.membroIds ?? []).includes(p.id)),
        [pessoas, aberto]);

    /* ─── Render ─────────────────────────────────────────────── */

    const COLUNAS = '1.3fr 1.1fr 2fr .6fr 78px';

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {equipes.length === 0 ? 'Nenhuma equipe cadastrada' : 'Nada com esse nome'}
            </div>
            <p className="lk-blank-text">
                {equipes.length === 0
                    ? 'Equipes agrupam vendedores para meta, ranking e acompanhamento.'
                    : 'Ajuste a busca para ver as outras.'}
            </p>
            {equipes.length === 0 && (
                <button type="button" className="lk-btn is-primary" onClick={abrirNova}>
                    <LuPlus size={14} /> Nova equipe
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">{inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}</span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} /></button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} /></button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Equipes"
                subtitle="Como o time comercial está dividido"
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && equipes.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={abrirNova}>
                            <LuPlus size={14} /> Nova equipe
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar por nome da equipe…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    <span className="lk-count">
                        {equipes.length} {equipes.length === 1 ? 'equipe' : 'equipes'}
                        {filtrados.length !== equipes.length && ` · ${filtrados.length} no filtro`}
                    </span>
                </div>

                {/* Celular: a grade de 5 colunas não cabe em 358px, então a linha
                    vira cartão — o nome da equipe, quem lidera e o tamanho dela. A
                    lista de membros fica no painel, um toque adiante. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(e => {
                                    const lider = e.liderNome || nomeDe(e.liderId);
                                    const ids = e.membroIds ?? [];
                                    /* A pergunta da lixeira continua NA lista, como no
                                       computador; o que muda é a forma, porque ela não
                                       cabe numa linha de cartão. */
                                    if (aConfirmar === e.id) return (
                                        <CardConfirm key={e.id}
                                            question={`Excluir "${e.nome}"? Os usuários continuam, sem esta equipe.`}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(e)} />
                                    );
                                    return (
                                        <ListCard
                                            key={e.id}
                                            titulo={e.nome}
                                            apoio={lider || 'sem líder'}
                                            estado={<span>{ids.length} {ids.length === 1 ? 'membro' : 'membros'}</span>}
                                            selected={aberto?.id === e.id}
                                            label={`Abrir ${e.nome}`}
                                            onOpen={() => abrirVer(e)}
                                            acoes={
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir equipe" danger
                                                    onClick={() => setAConfirmar(e.id)} />
                                            }
                                        />
                                    );
                                })}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Equipe</div><div className="lk-head">Líder</div>
                            <div className="lk-head">Membros</div><div className="lk-head">Total</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(e => {
                                    const lider = e.liderNome || nomeDe(e.liderId);
                                    const ids = e.membroIds ?? [];
                                    if (aConfirmar === e.id) return (
                                        <RowConfirm key={e.id}
                                            question={`Excluir "${e.nome}"? Os usuários continuam, sem esta equipe.`}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(e)} />
                                    );
                                    return (
                                        <ListRow key={e.id} columns={COLUNAS}
                                            selected={aberto?.id === e.id}>
                                            <span className="lk-cell lk-strong"><span>{e.nome}</span></span>
                                            <span className={`lk-cell ${lider ? 'lk-muted' : 'lk-empty-value'}`}>
                                                <span>{lider || 'sem líder'}</span>
                                            </span>
                                            <span className="lk-cell">
                                                {ids.length === 0
                                                    ? <span className="lk-empty-value">sem membros</span>
                                                    : <span>
                                                        {ids.slice(0, 2).map(i => (
                                                            <span className="lk-tag" key={i}>{nomeDe(i) ?? `#${i}`}</span>
                                                        ))}
                                                        {ids.length > 2 && <span className="lk-tag">+{ids.length - 2}</span>}
                                                      </span>}
                                            </span>
                                            <span className="lk-cell lk-muted"><span>{ids.length}</span></span>
                                            <RowActions>
                                                <RowAction icon={<LuEye size={14} />} label="Ver equipe"
                                                    onClick={() => abrirVer(e)} />
                                                <RowAction icon={<LuTrash2 size={14} />} label="Excluir equipe" danger
                                                    onClick={() => setAConfirmar(e.id)} />
                                            </RowActions>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>

                    {!isLoading && filtrados.length === 0 && listaVazia}

                    {!isLoading && filtrados.length > 0 && rodape}
                </div>
                )}
            </div>

            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                initials={iniciais(modo === 'editar' ? (rascunho.nome || 'Nova') : (aberto?.nome ?? ''))}
                title={modo === 'editar' ? (rascunho.nome.trim() || (ehNova ? 'Nova equipe' : 'Equipe')) : (aberto?.nome ?? '')}
                subtitle={aberto
                    ? `Equipe · ${(aberto.membroIds ?? []).length} ${(aberto.membroIds ?? []).length === 1 ? 'membro' : 'membros'}`
                    : 'Equipe'}
                editing={modo === 'editar'}
                onBack={ehNova ? undefined : () => setModo('ver')}
                actions={aberto && (
                    <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                        <LuPencil size={13} /> Editar
                    </button>
                )}
                footer={
                    <>
                        {!ehNova && (
                            <button type="button" className="rp-btn is-danger" onClick={() => setConfirmarNoPainel(true)} disabled={salvando}>
                                Excluir equipe
                            </button>
                        )}
                        <button type="button" className="rp-btn"
                            onClick={() => (ehNova ? fechar() : setModo('ver'))} disabled={salvando}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                {modo === 'ver' && aberto && (
                    <>
                        <PanelSection title="Liderança">
                            <div className="rp-kv">
                                <span className="rp-k">Líder</span>
                                {aberto.liderNome || nomeDe(aberto.liderId)
                                    ? <span className="rp-v">{aberto.liderNome || nomeDe(aberto.liderId)}</span>
                                    : <span className="rp-v is-empty">não definido</span>}
                            </div>
                        </PanelSection>

                        {/* Mesma operação da tela de Usuários, do outro lado. */}
                        <PanelSection title={`Membros · ${membrosDaAberta.length}`}>
                            {membrosDaAberta.length === 0
                                ? <p className="rp-none">Nenhum membro nesta equipe.</p>
                                : membrosDaAberta.map(m => {
                                    const ehLider = m.id === aberto.liderId;
                                    return (
                                        <div className="rp-file" key={m.id}>
                                            <span className="rp-file-ico"><LuUser size={12} /></span>
                                            <span className="rp-file-name">
                                                {m.nome}
                                                {ehLider && <span className="rp-badge is-open" style={{ marginLeft: 6 }}>líder</span>}
                                            </span>
                                            <button type="button" className="rp-file-act is-danger"
                                                disabled={ehLider || mexendo}
                                                title={ehLider
                                                    ? 'É líder desta equipe — troque o líder antes de removê-lo'
                                                    : 'Remover da equipe'}
                                                aria-label="Remover da equipe"
                                                onClick={() => mexerNosMembros(
                                                    ids => ids.filter(i => i !== m.id), `${m.nome} saiu da equipe.`)}>
                                                <LuX size={13} />
                                            </button>
                                        </div>
                                    );
                                })}

                            <div className="lk-filter" ref={refSeletor} style={{ display: 'block' }}>
                                <button type="button" className="rp-add" disabled={mexendo || !aberto.id}
                                    onClick={() => setSeletor(v => !v)}>
                                    <LuPlus size={13} /> {mexendo ? 'Salvando…' : 'Adicionar membro'}
                                </button>
                                {seletor && (
                                    <div className="lk-pop" role="menu" style={{ position: 'static', marginTop: 8 }}>
                                        {disponiveis.length === 0
                                            ? <button type="button" disabled>todos já estão nesta equipe</button>
                                            : disponiveis.map(p => (
                                                <button key={p.id} type="button"
                                                    onClick={() => mexerNosMembros(
                                                        ids => (ids.includes(p.id) ? ids : [...ids, p.id]),
                                                        `${p.nome} entrou na equipe.`)}>
                                                    <span>{p.nome}</span>
                                                </button>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </PanelSection>
                    </>
                )}

                {modo === 'editar' && (
                    <PanelSection title="Equipe">
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="eq-nome">Nome da equipe</label>
                            <input id="eq-nome" className={`rp-input ${erros.nome ? 'has-error' : ''}`} autoFocus
                                value={rascunho.nome} onChange={e => setRascunho(r => ({ ...r, nome: e.target.value }))} />
                            {erros.nome && <p className="rp-error">{erros.nome}</p>}
                        </div>
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="eq-lider">Líder</label>
                            <Select id="eq-lider" hasError={!!erros.liderId}
                                placeholder="Escolha…"
                                value={rascunho.liderId ? String(rascunho.liderId) : ''}
                                options={pessoas.map(p => ({ value: String(p.id), label: p.nome }))}
                                onChange={v => setRascunho(r => ({ ...r, liderId: Number(v) }))} />
                            {erros.liderId && <p className="rp-error">{erros.liderId}</p>}
                            <p className="rp-error" style={{ color: 'var(--rp-muted)' }}>
                                O líder entra como membro automaticamente.
                            </p>
                        </div>
                    </PanelSection>
                )}

                {/* Histórico (fase 4): nome, líder, quem entrou e quem saiu. */}
                {modo === 'ver' && aberto && (
                    <SecaoHistorico entidade="equipe" id={aberto.id} />
                )}
            </RecordPanel>

            <Dialog
                open={confirmarNoPainel}
                onClose={() => setConfirmarNoPainel(false)}
                title={`Excluir a equipe "${rascunho.nome}"?`}
                confirmLabel="Excluir equipe"
                danger
                busy={salvando}
                onConfirm={() => excluir()}
            >
                Os usuários continuam cadastrados — só deixam de pertencer a esta equipe.
            </Dialog>
        </div>
    );
};

export default TeamsPage;
